import { useFetcher, useLoaderData } from "react-router";
import { useEffect, useRef } from "react";

type SerializeFrom<T> = ReturnType<typeof useLoaderData<T>>;

export function useFetcherPolling<T extends {}>({
  url,
  interval,
  shouldPoll,
}: {
  url: string;
  interval: number;
  /** Determine whether polling should be active based on the current fetcher data.
   *  Called during render — return false to prevent any fetching. */
  shouldPoll: (data: SerializeFrom<T> | undefined) => boolean;
}) {
  // Key the fetcher by URL so that when the URL changes (e.g. navigating
  // between datasets), we get a fresh fetcher with data=undefined instead
  // of stale data from the previous URL.
  const fetcher = useFetcher<T>({ key: url });
  const pollingEnabled = shouldPoll(fetcher.data);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // Stop any existing interval when dependencies change
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    if (!pollingEnabled) {
      return;
    }

    const doFetch = () => {
      if (fetcher.state === "idle") {
        fetcher.load(url);
      }
    };

    // Fetch immediately
    doFetch();

    // Set up the polling interval
    intervalRef.current = setInterval(doFetch, interval);

    // Cleanup on unmount or when dependencies change
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
    // NOTE: fetcher is new object after rerender, will cause infinite loop.
    // url changing already gives us a fresh fetcher via the key.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, interval, pollingEnabled /* DO NOT INCLUDE fetcher */]);

  return fetcher;
}
